import { gql } from '@apollo/client'
// fragments
import { FIELD_FRAGMENT } from 'graphql/field/queries'
import { PERMISSIONS_FRAGMENT } from 'graphql/securityProfiles/queries'

export const RISK_TEMPLATE_FRAGMENT = gql`
  fragment RISK_TEMPLATE on RiskTemplate {
    id
    name
    archived
    private
    isGuestAccess
    isPublicSearchable
    generateCustomerUniqueId
    customerUniqueIdPrefix
    includeInAllAiContext
    aiEnabled
    aiContextDescription
    archiveOnDelete
    aiSuggestedPrompts
    fields {
      ...FIELD
      objectSecurity {
        id
        name
      }
    }
    fieldLinks {
      id
      riskViewId
      fieldLabel
      parentFieldId
      addFieldLabel
      targetEntityId
      position
      primary
      fieldGroups {
        id
        fieldId
        entityId
        parentFieldId
        groupedByEntityId
      }
      field {
        ...FIELD
        objectSecurity {
          id
          name
        }
      }
      linkingSettings {
        copyRecords
        createNewRecords
        referenceRecords
        mirrorParentPermissions
        excludeFieldsWhenCopying
      }
    }
    fieldGroups {
      id
      fieldId
      entityId
      parentFieldId
      groupedByEntityId
    }
    fieldValueMap {
      fieldId
      fieldName
      fieldValue
      fieldType
      fieldLabel
      fieldValueId
      parentFieldValueId
      createdAt
      fieldLinkId
      linkedFieldValue {
        fieldValue
        fieldValueId
      }
    }
    relatedRiskTemplatesIdsFromSystemProcesses
    formConditions {
      fieldId
      trigger {
        value
      }
      actions {
        fieldId
        type
        value
      }
    }
    permissions {
      ...PERMISSIONS
    }
  }
  ${FIELD_FRAGMENT}
  ${PERMISSIONS_FRAGMENT}
`

export const RISK_TEMPLATE_QUERY = gql`
  query RISK_TEMPLATE_QUERY($pagination: PaginationInput, $filter: RiskTemplateFilterInput) {
    riskTemplates(pagination: $pagination, filter: $filter) {
      data {
        ...RISK_TEMPLATE
      }
    }
  }
  ${RISK_TEMPLATE_FRAGMENT}
`

export const RISK_TEMPLATE_AI_PROMPTS_QUERY = gql`
  query RISK_TEMPLATE_AI_PROMPTS_QUERY($filter: RiskTemplateFilterInput) {
    riskTemplates(filter: $filter) {
      data {
        id
        aiSuggestedPrompts
      }
    }
  }
`

export const RISK_TEMPLATE_PB_QUERY = gql`
  query RISK_TEMPLATE_PUBLIC_QUERY($domain: String!, $pagination: PaginationInput, $filter: RiskTemplateFilterInput) {
    riskTemplatesPublic(domain: $domain, pagination: $pagination, filter: $filter) {
      data {
        ...RISK_TEMPLATE
      }
    }
  }
  ${RISK_TEMPLATE_FRAGMENT}
`

export const RISK_TEMPLATE_SELECT_QUERY = gql`
  query RISK_TEMPLATE_SELECT_QUERY($filter: RiskTemplateFilterInput, $pagination: PaginationInput) {
    riskTemplates(filter: $filter, pagination: $pagination) {
      data {
        id
        name
        archived
        fields {
          id
          label
          fieldType
          active
          parentFieldId
        }
      }
    }
  }
`

export const LINKED_RISK_TEMPLATES_QUERY = gql`
  query LINKED_RISK_TEMPLATES_QUERY($filter: RiskTemplateFilterInput, $pagination: PaginationInput) {
    riskTemplates(filter: $filter, pagination: $pagination) {
      data {
        id
        name
        archived
        aiEnabled
        fields {
          id
          label
          name
          fieldType
          active
          multiple
          parentFieldId
          source
          settings
          options {
            id
            active
            label
            value
          }
          includeInAiContext
          aiAutoPopulate
          aiIncludeHistory
        }
        fieldValueMap {
          fieldId
          fieldValue
        }
      }
    }
  }
`

export const RELATED_FORMS_SELECT_QUERY = gql`
  query RELATED_FORMS_SELECT_QUERY($filter: RelatedRiskTemplatesFilterInput!) {
    relatedRiskTemplates(filter: $filter, pagination: { pageTake: 1000, pageSkip: 0 }) {
      data {
        id
        name
        archived
        fields {
          id
          label
          fieldType
          active
          parentFieldId
        }
        fieldLinks {
          id
          primary
          fieldId
          position
          parentFieldId
          fieldLabel
          field {
            id
            label
            fieldType
            active
            parentFieldId
            entityId
          }
        }
        fieldGroups {
          id
          fieldId
          entityId
          parentFieldId
          groupedByEntityId
        }
      }
    }
  }
`

export const RISK_VIEW_TEMPLATE_SELECT_QUERY = gql`
  query RISK_VIEW_TEMPLATE_SELECT_QUERY($filter: RiskTemplateFilterInput, $pagination: PaginationInput) {
    riskTemplates(filter: $filter, pagination: $pagination) {
      data {
        id
        name
        archived
        fields {
          id
          label
          fieldType
          active
          parentFieldId
        }
        fieldLinks {
          id
          primary
          fieldId
          position
          parentFieldId
          fieldLabel
          field {
            id
            fieldType
            active
            parentFieldId
            entityId
          }
        }
        fieldGroups {
          id
          fieldId
          entityId
          parentFieldId
          groupedByEntityId
        }
      }
    }
  }
`

export const RELATED_FORMS_USERS_SELECT_QUERY = gql`
  query RELATED_FORMS_USERS_SELECT_QUERY(
    $userFilter: UserFilter
    $positionFilter: PositionFilterInput
    $order: OrderInput
    $pagination: PaginationInput
    $riskTemplatesFilter: RiskTemplateFilterInput
  ) {
    users(filter: $userFilter, order: $order, pagination: $pagination) {
      data {
        id
        active
        firstName
        lastName
        companyId
        displayName
      }
      pagination {
        pageSkip
        pageTake
        totalCount
      }
    }

    positions(filter: $positionFilter, pagination: $pagination) {
      data {
        id
        active
        description
        companyId
      }

      pagination {
        pageSkip
        pageTake
        totalCount
      }
    }

    riskTemplates(pagination: { pageTake: 10000, pageSkip: 0 }, filter: $riskTemplatesFilter) {
      data {
        id
        name
      }
    }
  }
`

export const RISK_VIEWS_QUERY = gql`
  query RISK_VIEWS_QUERY($pagination: PaginationInput, $filter: RiskViewFilterInput) {
    riskViews(pagination: $pagination, filter: $filter) {
      data {
        id
        createdAt
        updatedAt
        name
        isLinkedView
        riskTemplate {
          ...RISK_TEMPLATE
        }
        settings {
          fields {
            fieldId
            active
            fieldLinkId
            parentFieldId
            riskTemplateId
          }
        }
        customColumns {
          id
          name
          sources {
            fieldId
            templateId
          }
        }
        availableToUserOrPositionIds
      }
    }
  }
  ${RISK_TEMPLATE_FRAGMENT}
`

export const RISK_VIEWS_SELECT_QUERY = gql`
  query RISK_VIEWS_SELECT_QUERY($pagination: PaginationInput, $filter: RiskViewFilterInput) {
    riskViews(pagination: $pagination, filter: $filter) {
      data {
        id
        name
        riskTemplate {
          id
        }
      }
    }
  }
`

export const RISK_FRAGMENT = gql`
  fragment RISK on Risk {
    id
    private
    recurrence
    customerUniqueIdPostfix
    realRiskId
    parentRiskId
    parentRisk {
      id
      recurrence
      riskTemplate {
        id
        name
      }
    }
    isCreatedFromGuestAccess
    canUpdateFromSystemProcesses
    canDeleteFromSystemProcesses
    riskTemplate {
      id
      name
      archived
      aiEnabled
      fieldLinks {
        id
        primary
        fieldId
        position
        parentFieldId
      }
    }
    fieldValueMap {
      archived
      fieldId
      fieldName
      fieldValue
      fieldType
      fieldLabel
      fieldValueId
      parentFieldValueId
      createdAt
      fieldLinkId
      linkedFieldValue {
        fieldValue
        fieldValueId
      }
    }
    startOfTheNextPeriodAt
    createdAt
    updatedAt
    createdBy {
      id
      firstName
      lastName
      displayName
    }
    updatedBy {
      id
      firstName
      lastName
      displayName
    }
    permissions {
      ...PERMISSIONS
    }
  }
  ${PERMISSIONS_FRAGMENT}
`

export const RISKS_QUERY = gql`
  query RISKS_QUERY($filter: RiskFilterInput, $order: OrderInput, $pagination: PaginationInput) {
    risks(pagination: $pagination, filter: $filter, order: $order) {
      data {
        ...RISK
        mainRisk {
          id
          recurrence
          riskTemplate {
            id
            name
          }
        }
      }
      pagination {
        totalCount
        isInfiniteLoader
      }
    }
  }
  ${RISK_FRAGMENT}
`

export const RISK_VIEW_RISKS_QUERY = gql`
  query RISK_VIEW_RISKS_QUERY($filter: RiskFilterInput, $order: OrderInput, $pagination: PaginationInput) {
    risks(pagination: $pagination, filter: $filter, order: $order) {
      data {
        ... {
          ...RISK
          mainRisk {
            id
            recurrence
            riskTemplate {
              id
              name
            }
          }
        }
        inlineRelatedForms {
          id
          riskTemplateId
          fieldValueMap {
            fieldId
            fieldName
            fieldValue
            fieldType
            fieldLabel
            fieldValueId
            parentFieldValueId
            createdAt
            fieldLinkId
          }
        }
        customColumns {
          label
          fieldValue {
            createdAt
            entityId
            field {
              id
              source
              active
              name
              label
              fieldType
              options {
                id
                label
                value
              }
              multiple
              responseType
              settings
            }
            fieldId
            id
            value
          }
        }
      }
      pagination {
        totalCount
        isInfiniteLoader
      }
    }
  }
  ${RISK_FRAGMENT}
`

export const RISK_VIEW_RISKS_FETCH_MORE_QUERY = gql`
  query RISKS_QUERY_FETCH_MORE($filter: RiskFilterInput, $order: OrderInput, $pagination: PaginationInput) {
    risks(pagination: $pagination, filter: $filter, order: $order) {
      data {
        ... {
          ...RISK
          mainRisk {
            id
            recurrence
            riskTemplate {
              id
              name
            }
          }
        }
        inlineRelatedForms {
          id
          riskTemplateId
          fieldValueMap {
            fieldId
            fieldName
            fieldValue
            fieldType
            fieldLabel
            fieldValueId
            parentFieldValueId
            createdAt
            fieldLinkId
          }
        }
        customColumns {
          label
          fieldValue {
            createdAt
            entityId
            field {
              id
              source
              active
              name
              label
              fieldType
              options {
                id
                label
                value
              }
              multiple
              responseType
              settings
            }
            fieldId
            id
            value
          }
        }
      }
    }
  }
  ${RISK_FRAGMENT}
`

export const RISKS_PUBLIC_QUERY = gql`
  query RISKS_PUBLIC_QUERY(
    $domain: String!
    $filter: RiskFilterInput
    $order: OrderInput
    $pagination: PaginationInput
  ) {
    risksPublic(domain: $domain, pagination: $pagination, filter: $filter, order: $order) {
      data {
        ...RISK
      }
      pagination {
        totalCount
      }
    }
  }
  ${RISK_FRAGMENT}
`

export const RISKS_EXPORT_QUERY = gql`
  query RISKS_EXPORT_QUERY($filter: RiskFilterInput, $order: OrderInput, $pagination: PaginationInput) {
    risks(pagination: $pagination, filter: $filter, order: $order) {
      data {
        id
        parentRisk {
          id
          riskTemplate {
            customerUniqueIdPrefix
          }
          customerUniqueIdPostfix
        }
        customerUniqueIdPostfix
        riskTemplate {
          id
          name
          customerUniqueIdPrefix
          fields {
            ...FIELD
          }
          fieldLinks {
            id
            fieldLabel
            parentFieldId
            addFieldLabel
            targetEntityId
            position
            primary
            fieldGroups {
              id
              fieldId
              entityId
              parentFieldId
              groupedByEntityId
            }
            field {
              ...FIELD
              objectSecurity {
                id
                name
              }
            }
          }
          fieldGroups {
            id
            fieldId
            entityId
            parentFieldId
            groupedByEntityId
          }
          formConditions {
            fieldId
            trigger {
              value
            }
            actions {
              fieldId
              type
              value
            }
          }
        }
        fieldValueMap {
          fieldId
          fieldName
          fieldValue
          fieldType
          fieldLabel
          fieldValueId
          parentFieldValueId
          createdAt
          fieldLinkId
          linkedFieldValue {
            fieldValue
            fieldValueId
          }
        }
      }
    }
  }
  ${FIELD_FRAGMENT}
`

export const RELATED_RISKS_PERMISSIONS_QUERY = gql`
  query RELATED_RISKS_PERMISSIONS_QUERY($riskId: ID!, $riskTemplateId: ID!) {
    getRelatedFormsPermissions(riskId: $riskId, riskTemplateId: $riskTemplateId) {
      permissionsMap {
        riskId
        riskTemplateId
        permissions {
          ...PERMISSIONS
        }
      }
      riskTemplates {
        id
        name
        private
        aiEnabled
        archiveOnDelete
        fields {
          ...FIELD
          objectSecurity {
            id
            name
          }
        }
        fieldLinks {
          id
          fieldLabel
          parentFieldId
          addFieldLabel
          targetEntityId
          position
          primary
          riskViewId
          fieldGroups {
            id
            fieldId
            entityId
            parentFieldId
            groupedByEntityId
          }
          field {
            ...FIELD
            objectSecurity {
              id
              name
            }
          }
          linkingSettings {
            copyRecords
            createNewRecords
            referenceRecords
            mirrorParentPermissions
            excludeFieldsWhenCopying
          }
        }
        fieldGroups {
          id
          fieldId
          entityId
          parentFieldId
          groupedByEntityId
        }
        formConditions {
          fieldId
          trigger {
            value
          }
          actions {
            fieldId
            type
            value
          }
        }
      }
    }
  }
  ${FIELD_FRAGMENT}
  ${PERMISSIONS_FRAGMENT}
`

export const RISK_VIEW_FILTERS_QUERY = gql`
  query RISK_VIEW_FILTERS_QUERY($pagination: PaginationInput, $filter: FilterFilterInput, $order: OrderInput) {
    filters(pagination: $pagination, filter: $filter, order: $order) {
      data {
        id
        createdAt
        createdById
        name
        args
        targetId
      }
    }
  }
`

export const RISK_VIEW_FILTERS_SELECT_QUERY = gql`
  query RISK_VIEW_FILTERS_SELECT_QUERY($pagination: PaginationInput, $filter: FilterFilterInput) {
    filters(pagination: $pagination, filter: $filter) {
      data {
        id
        name
      }
    }
  }
`

export const GROUPED_FORMS_PERMISSIONS_QUERY = gql`
  query GROUPED_FORMS_PERMISSIONS_QUERY($riskId: ID!) {
    getGroupedFormsPermissions(riskId: $riskId) {
      groupedFormsPermissionsMap {
        primaryFieldValueId
        permissions {
          ...PERMISSIONS
        }
      }
    }
  }
  ${PERMISSIONS_FRAGMENT}
`

export const LINKED_FORMS_PERMISSIONS_QUERY = gql`
  query LINKED_FORMS_PERMISSIONS_QUERY($riskId: ID!) {
    getLinkedFormsPermissions(riskId: $riskId) {
      permissionsMap {
        permissions {
          ...PERMISSIONS
        }
        risk {
          id
          canDeleteFromSystemProcesses
          canUpdateFromSystemProcesses
          parentRiskId
        }
        riskId
      }
    }
  }
  ${PERMISSIONS_FRAGMENT}
`

export const GET_PARENT_FORM_PROPERTIES_QUERY = gql`
  query GET_PARENT_FORM_PROPERTIES_QUERY($riskId: ID!, $fieldMappings: [ParentFormFieldMapping!]!) {
    getParentFormProperties(riskId: $riskId, fieldMappings: $fieldMappings) {
      mainRisk {
        id
        customerUniqueIdPostfix
        riskTemplate {
          id
          name
        }
      }
      parentRisk {
        id
        customerUniqueIdPostfix
        riskTemplate {
          id
          name
        }
      }
      ancestors {
        id
        customerUniqueIdPostfix
        riskTemplate {
          id
          name
        }
      }
      fieldValues {
        entityId
        fieldName
        fieldLabel
        fieldValue
        fieldType
      }
    }
  }
`

export const GET_PARENT_FORM_FIELDS_QUERY = gql`
  query GET_PARENT_FORM_FIELDS_QUERY($riskId: ID!, $fieldMappings: [ParentFormFieldMapping!]!) {
    getParentFormProperties(riskId: $riskId, fieldMappings: $fieldMappings) {
      mainRisk {
        id
        customerUniqueIdPostfix
        riskTemplate {
          id
          name
        }
      }
      parentRisk {
        id
        customerUniqueIdPostfix
        riskTemplate {
          id
          name
        }
      }
      fieldValues {
        entityId
        fieldName
        fieldLabel
        fieldValue
        fieldType
      }
    }
  }
`
